import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Bell, BellRinging, Envelope, EnvelopeSimple, ArrowLeft,
  ShieldCheck, Key, ClipboardText, Ticket, Warning, Megaphone,
  SpinnerGap, Check, ArrowCounterClockwise, CaretRight, Info
} from '@phosphor-icons/react';

const PREFERENCE_CATEGORIES = [
  {
    id: 'access_requests',
    label: 'Access Requests',
    icon: Key,
    color: 'bg-amber-100 text-amber-600',
    description: 'Notifications about your access requests and approvals',
    preferences: [
      { key: 'access_request_submitted', label: 'Request Submitted', description: 'When you submit a new access request' },
      { key: 'access_request_approved', label: 'Request Approved', description: 'When your access request is approved' },
      { key: 'access_request_denied', label: 'Request Denied', description: 'When your access request is denied' },
      { key: 'access_request_pending_approval', label: 'Pending Your Approval', description: 'When someone needs your approval' },
    ]
  },
  {
    id: 'role_assignments',
    label: 'Role Assignments',
    icon: ShieldCheck,
    color: 'bg-purple-100 text-purple-600',
    description: 'Notifications about role changes and expirations',
    preferences: [
      { key: 'role_assigned', label: 'Role Assigned', description: 'When a new role is assigned to you' },
      { key: 'role_removed', label: 'Role Removed', description: 'When a role is removed from you' },
      { key: 'role_expiring_soon', label: 'Role Expiring Soon', description: 'Reminder before your role expires' },
      { key: 'role_expired', label: 'Role Expired', description: 'When your role has expired' },
    ]
  },
  {
    id: 'certifications',
    label: 'Certifications',
    icon: ClipboardText,
    color: 'bg-blue-100 text-blue-600',
    description: 'Notifications about certification campaigns',
    preferences: [
      { key: 'certification_review_required', label: 'Review Required', description: 'When you need to review access' },
      { key: 'certification_reminder', label: 'Certification Reminders', description: 'Reminder for pending reviews' },
      { key: 'certification_completed', label: 'Certification Completed', description: 'When a certification campaign ends' },
    ]
  },
  {
    id: 'provisioning',
    label: 'Provisioning',
    icon: Ticket,
    color: 'bg-rose-100 text-rose-600',
    description: 'Notifications about provisioning tickets',
    preferences: [
      { key: 'provisioning_ticket_assigned', label: 'Ticket Assigned', description: 'When a ticket is assigned to you' },
      { key: 'provisioning_ticket_completed', label: 'Ticket Completed', description: 'When your provisioning is complete' },
    ]
  },
  {
    id: 'sod',
    label: 'SoD Violations',
    icon: Warning,
    color: 'bg-orange-100 text-orange-600',
    description: 'Notifications about Separation of Duties',
    preferences: [
      { key: 'sod_violation_detected', label: 'Violation Detected', description: 'When an SoD violation is found' },
      { key: 'sod_violation_resolved', label: 'Violation Resolved', description: 'When an SoD violation is resolved' },
    ]
  },
  {
    id: 'system',
    label: 'System',
    icon: Megaphone,
    color: 'bg-slate-100 text-navy-600',
    description: 'System announcements and digests',
    preferences: [
      { key: 'system_announcements', label: 'System Announcements', description: 'Important platform updates' },
      { key: 'weekly_digest', label: 'Weekly Digest', description: 'Weekly summary of your access activity' },
    ]
  },
];

const PreferenceToggle = ({ prefKey, label, description, value, onChange, disabled }) => (
  <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
    <div className="flex-1 pr-4">
      <p className="text-sm font-medium text-navy-900">{label}</p>
      <p className="text-xs text-navy-500 mt-0.5">{description}</p>
    </div>
    <Switch
      checked={value}
      onCheckedChange={(checked) => onChange(prefKey, checked)}
      disabled={disabled}
      data-testid={`toggle-${prefKey}`}
    />
  </div>
);

const CategoryCard = ({ category, preferences, onToggle, expanded, onExpand }) => {
  const Icon = category.icon;
  const enabledCount = category.preferences.filter(p => preferences[p.key]).length;
  const totalCount = category.preferences.length;
  
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
      <button
        onClick={onExpand}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
        data-testid={`category-${category.id}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${category.color}`}>
            <Icon size={20} weight="duotone" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-navy-900">{category.label}</p>
            <p className="text-xs text-navy-500">{category.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={`text-[10px] rounded-full ${enabledCount === totalCount ? 'bg-emerald-100 text-emerald-700' : enabledCount === 0 ? 'bg-slate-100 text-navy-500' : 'bg-amber-100 text-amber-700'}`}>
            {enabledCount}/{totalCount} enabled
          </Badge>
          <CaretRight 
            size={16} 
            weight="bold" 
            className={`text-navy-400 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} 
          />
        </div>
      </button>
      
      {expanded && (
        <div className="px-5 pb-4 border-t border-slate-100">
          {category.preferences.map((pref) => (
            <PreferenceToggle
              key={pref.key}
              prefKey={pref.key}
              label={pref.label}
              description={pref.description}
              value={preferences[pref.key] ?? true}
              onChange={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default function NotificationPreferencesPage() {
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [originalPrefs, setOriginalPrefs] = useState({});

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const res = await API.get('/notifications/preferences');
      const prefs = res.data.preferences || {};
      setPreferences(prefs);
      setOriginalPrefs(prefs);
    } catch (err) {
      void 0;
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key, value) => {
    setPreferences(prev => {
      const updated = { ...prev, [key]: value };
      setHasChanges(JSON.stringify(updated) !== JSON.stringify(originalPrefs));
      return updated;
    });
  };

  const handleToggleDelivery = (key, value) => {
    handleToggle(key, value);
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await API.put('/notifications/preferences', { preferences });
      setOriginalPrefs(preferences);
      setHasChanges(false);
      toast.success('Notification preferences saved');
    } catch (err) {
      void 0;
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const resetPreferences = async () => {
    setSaving(true);
    try {
      const res = await API.post('/notifications/preferences/reset');
      setPreferences(res.data.preferences);
      setOriginalPrefs(res.data.preferences);
      setHasChanges(false);
      toast.success('Preferences reset to defaults');
    } catch (err) {
      void 0;
      toast.error('Failed to reset preferences');
    } finally {
      setSaving(false);
    }
  };

  const enableAll = () => {
    const allEnabled = {};
    PREFERENCE_CATEGORIES.forEach(cat => {
      cat.preferences.forEach(pref => {
        allEnabled[pref.key] = true;
      });
    });
    allEnabled.email_enabled = true;
    allEnabled.in_app_enabled = true;
    setPreferences(allEnabled);
    setHasChanges(JSON.stringify(allEnabled) !== JSON.stringify(originalPrefs));
  };

  const disableAll = () => {
    const allDisabled = {};
    PREFERENCE_CATEGORIES.forEach(cat => {
      cat.preferences.forEach(pref => {
        allDisabled[pref.key] = false;
      });
    });
    allDisabled.email_enabled = false;
    allDisabled.in_app_enabled = false;
    setPreferences(allDisabled);
    setHasChanges(JSON.stringify(allDisabled) !== JSON.stringify(originalPrefs));
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <SpinnerGap size={32} weight="bold" className="animate-spin text-orange-500 mx-auto" />
          <p className="text-sm text-navy-500 mt-3">Loading preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="notification-preferences-page" className="p-6 md:p-8 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-lg hover:bg-slate-100"
            data-testid="back-button"
          >
            <ArrowLeft size={20} weight="bold" />
          </Button>
          <div>
            <h1 className="text-3xl font-black text-navy-900 tracking-tight" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
              Notification Preferences
            </h1>
            <p className="text-sm text-navy-500 mt-1">Manage how and when you receive notifications</p>
          </div>
        </div>
        {hasChanges && (
          <Badge className="bg-amber-100 text-amber-700 rounded-full text-xs">
            Unsaved changes
          </Badge>
        )}
      </div>

      {/* Delivery Methods */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-slate-900 via-slate-800 to-orange-500" />
        <div className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <BellRinging size={20} weight="duotone" className="text-orange-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-navy-900 uppercase tracking-wider">Delivery Methods</h2>
              <p className="text-xs text-navy-500">Choose how you want to receive notifications</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-3">
                <Envelope size={20} weight="duotone" className="text-navy-600" />
                <div>
                  <p className="text-sm font-medium text-navy-900">Email Notifications</p>
                  <p className="text-xs text-navy-500">Receive notifications via email</p>
                </div>
              </div>
              <Switch
                checked={preferences.email_enabled ?? true}
                onCheckedChange={(checked) => handleToggleDelivery('email_enabled', checked)}
                data-testid="toggle-email"
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-3">
                <Bell size={20} weight="duotone" className="text-navy-600" />
                <div>
                  <p className="text-sm font-medium text-navy-900">In-App Notifications</p>
                  <p className="text-xs text-navy-500">See notifications in the platform</p>
                </div>
              </div>
              <Switch
                checked={preferences.in_app_enabled ?? true}
                onCheckedChange={(checked) => handleToggleDelivery('in_app_enabled', checked)}
                data-testid="toggle-in-app"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-navy-500">
          <Info size={14} weight="fill" />
          <span>Click on a category to expand and configure individual notifications</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={enableAll}
            className="text-xs rounded-lg"
            data-testid="enable-all-btn"
          >
            Enable All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={disableAll}
            className="text-xs rounded-lg"
            data-testid="disable-all-btn"
          >
            Disable All
          </Button>
        </div>
      </div>

      {/* Notification Categories */}
      <div className="space-y-3">
        {PREFERENCE_CATEGORIES.map((category) => (
          <CategoryCard
            key={category.id}
            category={category}
            preferences={preferences}
            onToggle={handleToggle}
            expanded={expandedCategory === category.id}
            onExpand={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
          />
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <Button
          variant="outline"
          onClick={resetPreferences}
          disabled={saving}
          className="rounded-lg text-navy-600 hover:text-navy-900"
          data-testid="reset-btn"
        >
          <ArrowCounterClockwise size={16} weight="bold" className="mr-2" />
          Reset to Defaults
        </Button>
        
        <Button
          onClick={savePreferences}
          disabled={saving || !hasChanges}
          className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg shadow-sm"
          data-testid="save-btn"
        >
          {saving ? (
            <>
              <SpinnerGap size={16} weight="bold" className="mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check size={16} weight="bold" className="mr-2" />
              Save Preferences
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
